import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MarkdownEditor } from '@/components/markdown/markdown-editor'
import { AvatarUploadBlock } from '@/components/users/avatar-upload-block'
import { useHandleAvailability, useMe, useUpdateProfile } from '@/features/users/use-users'
import type { ApiError } from '@/api/types'

const profileSchema = z.object({
  handle: z
    .string()
    .min(3, 'Handle must be 3–30 characters')
    .max(30, 'Handle must be 3–30 characters')
    .regex(/^[A-Za-z0-9_-]+$/, 'Letters, numbers, underscore, hyphen only'),
  displayName: z.string().min(1, 'Display name is required').max(100),
  bio: z.string().max(500).optional(),
})

type ProfileFormData = z.infer<typeof profileSchema>

export const SettingsProfilePage = () => {
  const { data: me, isLoading } = useMe()
  const update = useUpdateProfile()
  const [debouncedHandle, setDebouncedHandle] = useState<string | undefined>(undefined)

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: { handle: '', displayName: '', bio: '' },
  })

  useEffect(() => {
    if (me) {
      form.reset({
        handle: me.handle,
        displayName: me.displayName,
        bio: me.bio ?? '',
      })
    }
  }, [me, form])

  const handleValue = form.watch('handle')

  // Debounce: only check availability once the user pauses typing.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedHandle(handleValue), 400)
    return () => window.clearTimeout(t)
  }, [handleValue])

  const shouldCheck = Boolean(
    debouncedHandle && debouncedHandle !== me?.handle && debouncedHandle.length >= 3
  )
  const availability = useHandleAvailability(shouldCheck ? debouncedHandle : undefined)

  const onSubmit = (data: ProfileFormData) => {
    update.mutate(
      { handle: data.handle, displayName: data.displayName, bio: data.bio || undefined },
      {
        onSuccess: () => toast.success('Profile updated'),
        onError: (err) => {
          const apiError = err as unknown as ApiError
          toast.error(apiError.detail ?? 'Failed to update profile')
        },
      }
    )
  }

  if (isLoading) return null

  const handleAvailabilityMessage = (() => {
    if (!shouldCheck) return null
    if (availability.isFetching) return 'Checking…'
    if (!availability.data) return null
    if (availability.data.available) return 'Available'
    if (availability.data.reason === 'TAKEN') return 'Taken'
    if (availability.data.reason === 'RESERVED') return 'Reserved'
    if (availability.data.reason === 'INVALID') return 'Invalid format'
    return 'Unavailable'
  })()

  const submitDisabled =
    !form.formState.isDirty ||
    update.isPending ||
    (shouldCheck && availability.data?.available === false)

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit profile</h1>
        <p className="text-muted-foreground">
          Your handle, display name, and bio are visible at /u/{me?.handle ?? 'your-handle'}.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {me && (
              <AvatarUploadBlock
                handle={me.handle}
                displayName={me.displayName}
                avatarUrl={me.avatarUrl}
              />
            )}
            <div className="space-y-2">
              <Label htmlFor="handle">Handle</Label>
              <Input id="handle" {...form.register('handle')} />
              {form.formState.errors.handle && (
                <p className="text-xs text-destructive">{form.formState.errors.handle.message}</p>
              )}
              {handleAvailabilityMessage && (
                <p
                  className={
                    availability.data?.available
                      ? 'text-xs text-green-600 dark:text-green-400'
                      : 'text-xs text-destructive'
                  }
                >
                  {handleAvailabilityMessage}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display name</Label>
              <Input id="displayName" {...form.register('displayName')} />
              {form.formState.errors.displayName && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.displayName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <MarkdownEditor
                value={form.watch('bio') ?? ''}
                onChange={(next) => form.setValue('bio', next, { shouldDirty: true })}
                rows={4}
                maxChars={500}
                placeholder="Tell other makers a bit about yourself"
              />
              {form.formState.errors.bio && (
                <p className="text-xs text-destructive">{form.formState.errors.bio.message}</p>
              )}
            </div>

            <Button type="submit" disabled={submitDisabled}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
